import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Send, ExternalLink, Upload, Link as LinkIcon, Download } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ImageUpload } from "@/components/ImageUpload";

const EMPTY = {
  course_name: "", level: "Postgraduate", category_id: "", specialisations: [],
  description: "", duration: "", fee: "", currency: "INR", eligibility: "",
  mode: "Online", admission_cycle: "", course_url: "", image: "", brochure_url: "",
};
const STATUS_STYLE = {
  published: "bg-green-50 text-green-700", pending: "bg-amber-50 text-amber-700",
  draft: "bg-slate-100 text-slate-600", rejected: "bg-red-50 text-red-700",
};

export default function MyCourses() {
  const [courses, setCourses] = useState([]);
  const [filters, setFilters] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [urlOpen, setUrlOpen] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const BULK_COLUMNS = ["course_name", "category", "level", "duration", "fee", "currency",
    "mode", "specialisations", "eligibility", "admission_cycle", "course_url", "description"];

  const downloadTemplate = () => {
    const sample = 'Master of Business Administration,Online MBA,Postgraduate,2 years,180000,INR,Online,"Finance, Marketing",Bachelor\'s degree,January & July,https://university.edu/mba,An online MBA programme.';
    const csv = BULK_COLUMNS.join(",") + "\n" + sample + "\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "courses_template.csv"; a.click();
    URL.revokeObjectURL(a.href);
  };

  const uploadBulk = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/institution/courses/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.created} courses imported${data.skipped ? `, ${data.skipped} skipped` : ""}`);
      if (data.errors?.length) toast.message(data.errors.slice(0, 3).join(" | "));
      setBulkOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const importFromUrl = async () => {
    if (!importUrl.trim()) { toast.error("Enter a URL"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/institution/courses/import-url", { url: importUrl.trim() });
      toast.success(`${data.created} draft courses imported from your website. Review and publish them.`);
      setUrlOpen(false); setImportUrl(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const load = () => api.get("/institution/courses").then((r) => setCourses(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/public/filters").then((r) => setFilters(r.data)); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c, fee: c.fee ?? "" }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSpec = (id) => setForm((f) => ({ ...f,
    specialisations: f.specialisations.includes(id) ? f.specialisations.filter((s) => s !== id) : [...f.specialisations, id] }));

  const save = async (submitForReview) => {
    setSaving(true);
    const payload = { ...form, fee: form.fee === "" ? null : Number(form.fee),
      status: submitForReview ? "pending" : "draft" };
    try {
      if (editing) await api.put(`/institution/courses/${editing.id}`, payload);
      else await api.post("/institution/courses", payload);
      toast.success(editing ? "Course updated" : "Course created");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const submitCourse = async (id) => {
    try { await api.post(`/institution/courses/${id}/submit`); toast.success("Submitted for approval"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this course?")) return;
    try { await api.delete(`/institution/courses/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">My Courses</h1>
          <p className="text-sm text-slate-500">Add and manage your online programmes</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => setUrlOpen(true)} data-testid="import-url-btn"><LinkIcon className="mr-1.5 h-4 w-4" /> Import from URL</Button>
          <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-upload-btn"><Upload className="mr-1.5 h-4 w-4" /> Bulk Upload</Button>
          <Button onClick={openNew} className="bg-blue-800 hover:bg-blue-900" data-testid="add-course-btn"><Plus className="mr-1.5 h-4 w-4" /> Add Course</Button>
        </div>
      </div>

      {courses.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center">
          <p className="font-heading text-lg font-semibold text-slate-900">No courses yet</p>
          <p className="mt-1 text-slate-500">Add your first online programme to get started.</p>
          <Button onClick={openNew} className="mt-4 bg-blue-800 hover:bg-blue-900"><Plus className="mr-1.5 h-4 w-4" /> Add Course</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {courses.map((c) => (
            <div key={c.id} data-testid={`my-course-${c.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-heading font-semibold text-slate-900">{c.course_name}</h3>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_STYLE[c.status]}`}>{c.status}</span>
                </div>
                <p className="mt-0.5 text-sm text-slate-500">{c.level} · {c.duration} · {c.mode}</p>
              </div>
              <div className="flex items-center gap-1">
                {(c.status === "draft" || c.status === "rejected") && (
                  <Button size="sm" variant="ghost" onClick={() => submitCourse(c.id)} title="Submit for approval" data-testid={`submit-course-${c.id}`}>
                    <Send className="h-4 w-4 text-blue-800" />
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-course-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(c.id)} data-testid={`delete-course-${c.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit Course" : "Add Course"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <F label="Course Name"><Input value={form.course_name} onChange={(e) => set("course_name", e.target.value)} data-testid="course-name" /></F>
            <div className="grid gap-4 sm:grid-cols-2">
              <F label="Level">
                <Select value={form.level} onValueChange={(v) => set("level", v)}>
                  <SelectTrigger data-testid="course-level"><SelectValue /></SelectTrigger>
                  <SelectContent>{(filters?.levels || []).map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Category">
                <Select value={form.category_id} onValueChange={(v) => set("category_id", v)}>
                  <SelectTrigger data-testid="course-category"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="max-h-60">{(filters?.categories || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Duration"><Input value={form.duration} onChange={(e) => set("duration", e.target.value)} placeholder="e.g. 2 Years" /></F>
              <F label="Mode">
                <Select value={form.mode} onValueChange={(v) => set("mode", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{(filters?.modes || ["Online"]).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Total Fee"><Input type="number" value={form.fee} onChange={(e) => set("fee", e.target.value)} data-testid="course-fee" /></F>
              <F label="Currency">
                <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="INR">INR (₹)</SelectItem><SelectItem value="USD">USD ($)</SelectItem></SelectContent>
                </Select>
              </F>
              <F label="Admission Cycle"><Input value={form.admission_cycle} onChange={(e) => set("admission_cycle", e.target.value)} placeholder="e.g. Jan & Jul" /></F>
              <F label="Course URL"><Input value={form.course_url} onChange={(e) => set("course_url", e.target.value)} placeholder="https://" /></F>
            </div>
            <F label="Specialisations">
              <div className="flex flex-wrap gap-2">
                {(filters?.specialisations || []).map((s) => (
                  <button type="button" key={s.id} onClick={() => toggleSpec(s.id)}
                    className={`rounded-full border px-3 py-1 text-sm transition ${
                      form.specialisations.includes(s.id) ? "border-blue-800 bg-blue-800 text-white" : "border-slate-200 text-slate-600 hover:border-blue-300"}`}>
                    {s.name}
                  </button>
                ))}
              </div>
            </F>
            <F label="Eligibility"><Input value={form.eligibility} onChange={(e) => set("eligibility", e.target.value)} /></F>
            <F label="Course Image"><ImageUpload value={form.image} onChange={(v) => set("image", v)} label="Course Image" /></F>
            <F label="Description"><Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="course-description" /></F>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="save-draft-btn">Save as Draft</Button>
            <Button className="bg-blue-800 hover:bg-blue-900" onClick={() => save(true)} disabled={saving} data-testid="submit-review-btn">
              {saving ? "Saving..." : "Submit for Approval"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Bulk Upload Courses (CSV / Excel)</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Upload a .csv or .xlsx file. The first row must contain these exact column headings (only <b>course_name</b> is required):</p>
            <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
              {BULK_COLUMNS.join(", ")}
            </div>
            <p className="text-xs text-slate-500"><b>category</b> and <b>specialisations</b> must match names used on the site (comma-separated for multiple specialisations). Imported courses are saved as drafts for review.</p>
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template-btn"><Download className="mr-1.5 h-4 w-4" /> Download CSV template</Button>
            <div>
              <Label className="mb-1.5 block">Choose file</Label>
              <input type="file" accept=".csv,.xlsx,.xls" onChange={uploadBulk} disabled={busy} data-testid="bulk-file-input"
                className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-blue-800 file:px-3 file:py-2 file:text-white hover:file:bg-blue-900" />
            </div>
            {busy && <p className="text-sm text-blue-800">Uploading and importing...</p>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={urlOpen} onOpenChange={setUrlOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Import Courses from Your Website</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Paste a page URL that lists your courses. We'll fetch it and create <b>draft</b> courses with their names and links for you to review, complete and publish. Works best on simple listing pages.</p>
            <div>
              <Label className="mb-1.5 block">Course listing page URL</Label>
              <Input value={importUrl} onChange={(e) => setImportUrl(e.target.value)} placeholder="https://your-university.edu/courses" data-testid="import-url-input" />
            </div>
            {busy && <p className="text-sm text-blue-800">Fetching and importing...</p>}
          </div>
          <DialogFooter>
            <Button className="bg-blue-800 hover:bg-blue-900" onClick={importFromUrl} disabled={busy} data-testid="import-url-submit">{busy ? "Importing..." : "Import Courses"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children }) {
  return <div><Label className="mb-1.5 block">{label}</Label>{children}</div>;
}
